import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Editable from "./Editable";
import {
  ExpandMore,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useDynamicComponents } from "../../hooks/useDynamicComponents";
import { STEP_ICONS } from "../dynamic/StepIcons";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";
import { DragItem } from "./DragItem";
import PropList from "./PropList";
import DeleteDialog from "./DeleteDialog";
import AddPageDialog from "./AddPageDialog";

const EditableStep = ({
  step,
  hasPrevious,
  hasNext,
  handleMove,
  handleChange,
}) => {
  const [tempName, setTempName] = useState("");
  const [tempPages, setTempPages] = useState([]);
  const [isEdited, setIsEdited] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [isDraggable, setIsDraggable] = useState(false);

  const { DYNAMIC_COMPONENTS } = useDynamicComponents();

  const inputRef = useRef();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const resetStep = () => {
    setTempName(step.stepName);
    step.pages.sort((a, b) => a.pageNumber - b.pageNumber);
    // Clean copy of pages so the original state persists
    // Add an id to temporary pages for sorting purposes
    setTempPages([...step.pages.map((page) => ({ ...page, id: uid() }))]);
  };

  useEffect(() => {
    resetStep();
  }, [step]);

  // Remove temporary ids used for sorting
  const removeIdFromObjArray = (arr) => {
    return [
      ...arr.map((obj) => {
        let { id: _, ...rest } = { ...obj };
        return { ...rest };
      }),
    ];
  };

  const resortPages = (pages) => {
    const sorted = [
      ...pages.map((page, index) => {
        return {
          ...page,
          pageNumber: index + 1,
        };
      }),
    ];
    return sorted;
  };

  const handleSave = () => {
    // Clean-save pages to not obstruct change-comparison
    const updatedStep = {
      ...step,
      stepName: tempName,
      pages: [...removeIdFromObjArray(tempPages)],
    };

    handleChange(updatedStep);
    if (isDraggable) {
      setIsDraggable(false);
    }
  };

  const handleAbort = () => {
    resetStep();
    setIsDraggable(false);
  };

  const handlePropChange = (props, pageNumber) => {
    const updatedPages = tempPages.map((page) => {
      if (page.pageNumber === pageNumber) {
        return {
          ...page,
          props: { ...props },
        };
      }
      return page;
    });
    setTempPages(updatedPages);
  };

  const handleAdd = (component) => {
    // Create page with custom uid for sorting
    const page = {
      pageNumber: tempPages.length + 2,
      component: component,
      props: {},
      id: uid(),
    };
    setTempPages(resortPages([...tempPages, page]));
  };

  const handleDelete = (pageNumber) => {
    const updatedPages = tempPages.filter(
      (page) => page.pageNumber !== pageNumber
    );
    setTempPages(resortPages(updatedPages));
  };

  // Compare displayed step values with the initial step values to see if step has been edited
  const checkChanges = () => {
    if (tempName === "" || tempPages.length === 0) {
      return;
    }
    // Remove the temporary ids used for sorting before comparing -> Objects would always differ otherwise
    const tPagesCopy = removeIdFromObjArray(tempPages);
    // Compare name and pages
    if (tempName !== step.stepName || !pagesEqual(tPagesCopy, step.pages)) {
      setIsEdited(true);
    } else {
      setIsEdited(false);
    }
  };

  useEffect(() => {
    checkChanges();
  }, [tempName, tempPages]);

  const arrayMove = (arr, fromIndex, toIndex) => {
    var element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    const oldIndex = tempPages.findIndex((page) => page.id === active.id);
    const newIndex = tempPages.findIndex((page) => page.id === over.id);
    const arr = arrayMove(tempPages, oldIndex, newIndex);
    const sorted = resortPages(arr);
    setTempPages(sorted);

    setActiveId(null);
    checkChanges();
  };

  const toggleDraggable = () => {
    setIsDraggable(!isDraggable);
  };

  return (
    <>
      {step && (
        <Card>
          <CardHeader
            avatar={STEP_ICONS[step.icon]({})}
            title={
              <>
                <div className="step-title-name">
                  <Typography variant="h4" component="span">
                    {step.stepNumber + ". "}
                    {!step.editable && step.stepName}
                  </Typography>
                  {step.editable && (
                    <Editable
                      text={tempName}
                      placeholder="Schritt"
                      type="input"
                      childRef={inputRef}
                    >
                      <TextField
                        variant="standard"
                        ref={inputRef}
                        type="text"
                        name="step-title"
                        placeholder="Schrittname ändern"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        autoFocus
                      />
                    </Editable>
                  )}
                </div>
                {step.movable && (
                  <div className="step-title-actions">
                    {hasPrevious(step.stepNumber) && (
                      <Tooltip title="Schritt mit vorherigem Schritt tauschen">
                        <IconButton
                          color="primary"
                          aria-label="step-move-forward"
                          onClick={() =>
                            handleMove(step.stepNumber, step.stepNumber - 1)
                          }
                        >
                          <KeyboardDoubleArrowLeft />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasNext(step.stepNumber) && (
                      <Tooltip title="Schritt mit nächstem Schritt tauschen">
                        <IconButton
                          color="primary"
                          aria-label="step-move-back"
                          onClick={() =>
                            handleMove(step.stepNumber, step.stepNumber + 1)
                          }
                        >
                          <KeyboardDoubleArrowRight />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                )}
              </>
            }
          />
          <CardContent>
            <Typography gutterBottom variant="h5" component="span">
              Seiten
            </Typography>

            {step.editable && (
              <div className="step-page-action">
                <Button variant="outlined" onClick={toggleDraggable}>
                  {isDraggable ? "Stop" : "Umordnen"}
                </Button>
                <AddPageDialog
                  buttonText="Seite hinzufügen"
                  onSubmit={handleAdd}
                />
              </div>
            )}
            <Divider orientation="horizontal" />
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tempPages}
                strategy={verticalListSortingStrategy}
              >
                {tempPages.map((page) => {
                  return (
                    <SortableItem
                      key={`step-${step.stepNumber}-page-${page.pageNumber}-sortable-${page.id}`}
                      id={page.id}
                      isDraggable={isDraggable}
                      className={`${isDraggable ? "draggable-item" : ""}`}
                    >
                      {step.editable && !isDraggable ? (
                        <Accordion
                          key={`step-${step.stepNumber}-page-${page.pageNumber}`}
                          className="step-page-header"
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <div>
                              <Typography variant="h6" className="span">
                                {DYNAMIC_COMPONENTS[page.component].name}
                              </Typography>
                              <Typography variant="subtitle" component="span">
                                {page.props.questionaireContent &&
                                  ` (${page.props.questionaireContent.title})`}
                                {page.props.fileName &&
                                  ` (${page.props.fileName})`}
                              </Typography>
                            </div>
                          </AccordionSummary>
                          {page.props &&
                            DYNAMIC_COMPONENTS[page.component].props && (
                              <AccordionDetails>
                                <PropList
                                  id={`${step.stepNumber}-${page.pageNumber}-props`}
                                  props={page.props}
                                  propInfo={
                                    DYNAMIC_COMPONENTS[page.component].props
                                  }
                                  handlePropChange={(updatedProps) =>
                                    handlePropChange(
                                      updatedProps,
                                      page.pageNumber
                                    )
                                  }
                                />
                              </AccordionDetails>
                            )}
                          <AccordionActions>
                            <DeleteDialog
                              buttonText="Löschen"
                              onSubmit={() => handleDelete(page.pageNumber)}
                            />
                          </AccordionActions>
                        </Accordion>
                      ) : (
                        <Paper
                          key={`step-${step.stepNumber}-page-${page.pageNumber}`}
                          elevation={1}
                          className="step-page-header paper"
                        >
                          <div>
                            <Typography variant="h6" className="span">
                              {DYNAMIC_COMPONENTS[page.component].name}
                            </Typography>
                            <Typography variant="subtitle" component="span">
                              {page.props.questionaireContent &&
                                ` (${page.props.questionaireContent.title})`}
                              {page.props.fileName &&
                                ` (${page.props.fileName})`}
                            </Typography>
                          </div>
                        </Paper>
                      )}
                    </SortableItem>
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeId ? (
                  <DragItem id={activeId}>
                    <Paper elevation={1} className="step-page-header paper">
                      <div>
                        <Typography variant="h6" className="span">
                          {
                            DYNAMIC_COMPONENTS[
                              tempPages.find((page) => page.id === activeId)
                                .component
                            ].name
                          }
                        </Typography>
                        <Typography variant="subtitle" component="span">
                          {tempPages.find((page) => page.id === activeId).props
                            .questionaireContent &&
                            ` (${
                              tempPages.find((page) => page.id === activeId)
                                .props.questionaireContent.title
                            })`}
                          {tempPages.find((page) => page.id === activeId).props
                            .fileName &&
                            ` (${
                              tempPages.find((page) => page.id === activeId)
                                .props.fileName
                            })`}
                        </Typography>
                      </div>
                    </Paper>
                  </DragItem>
                ) : null}
              </DragOverlay>
            </DndContext>
          </CardContent>
          {isEdited && (
            <CardActions>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Speichern
              </Button>
              <Button variant="outlined" onClick={handleAbort}>
                Abbrechen
              </Button>
            </CardActions>
          )}
        </Card>
      )}
    </>
  );
};

// Recursive check if objects are equal
const objectsEqual = (a, b) => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date)
    return a.getTime() === b.getTime();
  if (!a || !b || (typeof a !== "object" && typeof b !== "object"))
    return a === b;
  if (a === null || a === undefined || b === null || b === undefined)
    return false;
  if (a.prototype !== b.prototype) return false;
  let keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => objectsEqual(a[k], b[k]));
};

// Check if either the order or the content of the pages have changed
const pagesEqual = (arrA, arrB) => {
  if (arrA.length !== arrB.length) {
    return false;
  }
  for (let i = 0; i < arrA.length; i++) {
    // Recursive check if anything relating the page has changed
    if (!objectsEqual(arrA[i], arrB[i])) {
      return false;
    }
  }
  return true;
};

const uid = () => {
  return (
    Date.now().toString(36) +
    Math.random().toString(36).substring(2, 12).padStart(12, 0)
  );
};

export default EditableStep;
